/**
 * Resource Processor - Validates and optimizes article resources
 * Enforces: max 3 articles, PDFs prioritized, no dummy links
 */

export interface ArticleResource {
  title: string;
  url: string;
  preview?: string;
}

const DUMMY_PATTERNS = [
  'example.com',
  'example.org',
  'example.net',
  'test.com',
  'placeholder',
  'https://example',
  'http://example',
  'yourlink',
  'insert-link',
  'pending',
  'coming soon',
  'tbd',
];

/**
 * Check if URL is a PDF file
 */
export const isPdfUrl = (url: string): boolean => {
  try {
    const lowerUrl = url.toLowerCase();
    return lowerUrl.endsWith('.pdf') || lowerUrl.includes('pdf');
  } catch {
    return false;
  }
};

/**
 * Check if a resource URL is valid (not dummy/placeholder)
 */
export const isValidResourceUrl = (url: string | undefined): boolean => {
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return false;
  }

  const lowerUrl = url.toLowerCase();

  // Check for dummy patterns
  if (DUMMY_PATTERNS.some((pattern) => lowerUrl.includes(pattern))) {
    return false;
  }

  // Check for valid URL format
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Process and optimize articles array
 * - Filters out invalid/dummy URLs
 * - Sorts to prioritize PDFs
 * - Limits to max 3 articles
 */
export const processArticles = (articles: any[] | undefined): ArticleResource[] => {
  if (!articles || !Array.isArray(articles)) {
    return [];
  }

  // Filter valid resources
  const validArticles: ArticleResource[] = articles
    .filter(
      (article) =>
        article &&
        article.title &&
        typeof article.title === 'string' &&
        isValidResourceUrl(article.url)
    )
    .map((article) => ({
      title: article.title.trim(),
      url: article.url.trim(),
      preview: article.preview ? article.preview.trim() : undefined,
    }));

  // Sort: PDFs first, then others
  const sorted = validArticles.sort((a, b) => {
    const aPdf = isPdfUrl(a.url) ? 0 : 1;
    const bPdf = isPdfUrl(b.url) ? 0 : 1;
    return aPdf - bPdf;
  });

  // Limit to max 3
  return sorted.slice(0, 3);
};

/**
 * Process full resources object
 */
export const processResources = (resources: any): any => {
  if (!resources || typeof resources !== 'object') {
    return { articles: [], video: undefined };
  }

  return {
    articles: processArticles(resources.articles),
    video: resources.video || undefined,
  };
};
